import * as React from "react";
import useField, { useFieldProps } from "./useField";
import useApi, { API } from "../useApi";
import { KeyLabel, APIResponseType } from "src/Library/Types";
import {
  NotFoundContent,
  DropDown,
  Options,
} from "src/components/common/SelectParts";

export type SelectAction = {
  onClick: (...args) => void;
  label: React.ReactNode;
};

export type useSelectProps<A> = useFieldProps<A> & {
  forcePrefill?: boolean;
};

export const useSelect = <A, B, C, D>(
  props: useSelectProps<A>,
  defaultApi: API<B, C>,
  mapKeyLabel: (param?: C) => (KeyLabel & D)[],
  defaultMapSearchArg?: (queryString: string) => B,
) => {
  const { forcePrefill } = props;
  const field = useField(props);
  const searchOptionsApi = useApi(defaultApi, 300, true);
  const [mapSearchArg, setMapSearchArg] = React.useState(() => defaultMapSearchArg);
  const [options, setOptions] = React.useState([] as (KeyLabel & D)[]);
  const [actions, setActions] = React.useState([] as SelectAction[]);
  const refs = React.useRef({ localSearchTimeout: undefined as NodeJS.Timeout | undefined });

  React.useEffect(() => {
    !mapSearchArg && refresh();
  }, []);

  React.useEffect(() => {
    if (searchOptionsApi.api === defaultApi) return;  // TODO: can reduce number of api calls but can cause problem while changing api
    refresh();
  }, [mapSearchArg, searchOptionsApi.api]);

  React.useEffect(() => {
    const status = searchOptionsApi.status;
    const data = searchOptionsApi.data;

    if (status === "FINISHED") {
      setOptions(mapKeyLabel(data));
    }

    if (status === "FAILED") {
      setOptions([]);
    }
  }, [searchOptionsApi.status, searchOptionsApi.data]);

  /** To enable prefilling in case options change depending on some other field */
  React.useEffect(() => {
    if (forcePrefill) {
      const isPrefillValueInOptions = options?.some((opt) => opt.key === props.value);
      if (isPrefillValueInOptions && !field.value) field.setValue(props.value);
    }
  }, [options?.length]);

  const onSearch = (queryString: string) => {
    mapSearchArg ? searchOptionsApi.call(mapSearchArg(queryString)) : handleLocalSearch(queryString);
  }

  const handleLocalSearch = (queryString: string) => {
    if (refs.current.localSearchTimeout) {
      clearTimeout(refs.current.localSearchTimeout);
    }
    refs.current.localSearchTimeout = setTimeout(() => {
      const data = searchOptionsApi.data;
      const newOptions = mapKeyLabel(data).filter(el => {
        return Object.keys(el).some(label => {
          return typeof el?.label === 'string' && el?.label?.toLowerCase().includes(queryString.toLowerCase());
        });
      });
      setOptions(newOptions);
    }, 300);
  };
  
  const onFocus = (e) => {
    onSearch("");
  };

  const filterOption = (inputValue, option) => true;

  const notFoundContent = NotFoundContent(
    searchOptionsApi.status === "RUNNING"
  );

  const dropdownRender = DropDown(actions);
  const children = Options(options);

  const decorator = (
    child: React.ReactElement,
  ): React.ReactNode => {
    return field.decorator(
      React.cloneElement(child, {
        ...child.props,
        onSearch: child.props.onSearch || onSearch,
        onFocus: child.props.onFocus || onFocus,
        filterOption: child.props.filterOption || filterOption,
        notFoundContent: child.props.notFoundContent || notFoundContent,
        dropdownRender: child.props.dropDownRender || dropdownRender,
        children: searchOptionsApi.status === "RUNNING" ? [] : (child.props.children || children),
      })
    );
  };

  const refresh = () => {
    mapSearchArg ? searchOptionsApi.call(mapSearchArg('')) : searchOptionsApi.call();
  };

  const setValueHandler = async (value) => {
    // if(!searchOptionsApi.data) {
    //   refresh();
    // }
    field.setValue(value);
  };

  const handleSetMapSearchArg = (mapSearchArg: (queryString: string) => B) => {
    setMapSearchArg(() => mapSearchArg);
  };

  const optionData = options?.find(el => el.key === field.value);

  return {
    ...field,
    setValue: setValueHandler,
    decorator,
    options,
    actions,
    setActions,
    refresh,
    setApi: searchOptionsApi.setApi,
    setMapSearchArg: handleSetMapSearchArg,
    optionData,
    setOptions,
    fieldType: 'select',
  };
};

export const useStaticSelect = <OptionsDataType, A>(
  props: useSelectProps<A> & { defaultOptions?: KeyLabel[] },
) => {
  const { forcePrefill, defaultOptions } = props;
  const field = useField(props);
  const [options, setOptions] = React.useState(defaultOptions || []);
  const [optionsData, setOptionsData] = React.useState((defaultOptions || []) as unknown as OptionsDataType);
  const [actions, setActions] = React.useState([] as SelectAction[]);

  /** To enable prefilling in case options change depending on some other field */
  React.useEffect(() => {
    if (forcePrefill) {
      const isPrefillValueInOptions = options?.some((opt) => opt.key === props.value);
      if (isPrefillValueInOptions) field.setValue(props.value);
    }
  }, [options?.length]);

  const dropdownRender = DropDown(actions);

  const children = Options(options);
  const decorator = (child: React.ReactElement): React.ReactNode => {
    return field.decorator(
      React.cloneElement(child, {
        ...child.props,
        children: child.props.children || children,
        dropdownRender: child.props.dropdownRender || dropdownRender,
      })
    );
  };

  const setOptionsDataWrapper = (data: OptionsDataType, mapKeyLabel: (param?: OptionsDataType) => KeyLabel[]) => {
    setOptionsData(data);
    setOptions(mapKeyLabel(data) || []);
  };

  const optionData = options?.find(el => el.key === field.value);

  return {
    ...field,
    decorator,
    options,
    optionsData,
    setOptionsData: setOptionsDataWrapper,
    actions,
    setActions,
    optionData,
    fieldType: 'select',
  };
};
